import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Spinner, Alert, Col } from 'react-bootstrap';
import { inventoryItemAPI } from '../api/inventoryItemAPI';
import { warehouseAPI } from '../api/warehouseAPI';
import { locationAPI } from '../api/locationAPI';
import { settingsAPI } from '../api/settingsAPI';
import { formatErrorResponse } from '../utils/errorHandlers';
import { usePermissions } from '../contexts/PermissionContext';
import Select from 'react-select';
import QuantityPicker from './QuantityPicker';
import '../styles/modal.css';

function BulkMoveModal({
    show,
    onHide,
    selectedItems,
    warehouse,
    onSuccess,
    inventoryItems = [] // Default to empty array if not provided
}) {
    const { hasPermission } = usePermissions();
    const [locations, setLocations] = useState([]);
    const [selectedLocation, setSelectedLocation] = useState(null);
    const [comments, setComments] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [selectedItemDetails, setSelectedItemDetails] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [itemQuantities, setItemQuantities] = useState({});
    const [warehouses, setWarehouses] = useState([]);
    const [selectedWarehouse, setSelectedWarehouse] = useState(null);
    const [systemSettings, setSystemSettings] = useState({
        enable_warehouse_transit: true // default to true for safety
    });
    const [isInitialLoad, setIsInitialLoad] = useState(true);
    const [activeItems, setActiveItems] = useState([]);
    
    // Initialize active items and quantities when selectedItems change
    useEffect(() => {
        if (selectedItems && selectedItems.length > 0) {
            setActiveItems([...selectedItems]);
            const initialQuantities = selectedItems.reduce((acc, item) => {
                acc[item.id] = '';
                return acc;
            }, {});
            setItemQuantities(initialQuantities);
        }
    }, [selectedItems]);

    // Fetch system settings
    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const settings = await settingsAPI.get();
                setSystemSettings(settings);
            } catch (error) {
                console.error('Error fetching system settings:', error);
                // Keep default value if fetch fails
            }
        };
        fetchSettings();
    }, []);

    // Fetch warehouses
    useEffect(() => {
        const fetchWarehouses = async () => {
            try {
                const response = await warehouseAPI.getAll();
                setWarehouses(response);
            } catch (error) {
                console.error('Error fetching warehouses:', error);
            }
        };
        fetchWarehouses();
    }, []);
    
    // Fetch locations when selected warehouse changes
    useEffect(() => {
        const fetchLocations = async () => {
            // Return early if no warehouse selected or no valid ID
            if (!selectedWarehouse?._id) {
                setLocations([]);
                setSelectedLocation(null);
                return;
            }

            try {
                const locationsData = await locationAPI.getAll(selectedWarehouse._id);
                setLocations(locationsData || []);

                // Only auto-select FLOOR location if this is NOT the initial load
                if (!isInitialLoad) {
                    // Try to automatically select location ending with '-FLOOR'
                    const floorLocation = locationsData.find(l => l.name.toUpperCase().endsWith('-FLOOR'));
                    if (floorLocation) {
                        setSelectedLocation({
                            value: floorLocation._id,
                            label: floorLocation.name
                        });
                    } else {
                        setSelectedLocation(null); // Reset location when warehouse changes and no FLOOR location found
                    }
                }
            } catch (error) {
                console.error('Error fetching locations:', error);
                setError('Failed to load locations');
                setLocations([]);
            }
        };

        fetchLocations();
    }, [selectedWarehouse, isInitialLoad]);
    
    // Fetch locations and item details when the modal opens
    useEffect(() => {
        const fetchData = async () => {
            if (!show) return;
            
            try {
                setIsLoading(true);
                
                // Set initial warehouse and ensure it has the correct structure
                if (warehouse) {
                    const initialWarehouse = {
                        _id: warehouse.id || warehouse._id, // Handle both id and _id formats
                        name: warehouse.name
                    };
                    setSelectedWarehouse(initialWarehouse);
                    
                    // Fetch locations for the initial warehouse
                    const locationsData = await locationAPI.getAll(initialWarehouse._id);
                    setLocations(locationsData || []);
                }
                
                // Fetch item details
                if (selectedItems.length > 0) {
                    const itemIds = selectedItems.map(item => item.id).join(',');
                    const response = await inventoryItemAPI.getByIds(itemIds);
                    
                    // Extract items array from response
                    const items = response?.results || [];
                    
                    if (items.length > 0) {
                        setSelectedItemDetails(items);
                    } else {
                        setError('Failed to load item details - no data returned');
                        setSelectedItemDetails([]);
                    }
                }
            } catch (error) {
                console.error('Error fetching data:', error);
                setError('Failed to load data');
            } finally {
                setIsLoading(false);
            }
        };
        
        if (show) {
            // Reset state when modal opens
            setSelectedLocation(null);
            setComments('');
            setError('');
            setIsSubmitting(false);
            setSelectedItemDetails([]);
            setIsInitialLoad(true); // Mark as initial load
            setActiveItems([...selectedItems]); // Reset active items to original selection
            
            // Reset item quantities to empty values
            const initialQuantities = selectedItems.reduce((acc, item) => {
                acc[item.id] = '';
                return acc;
            }, {});
            setItemQuantities(initialQuantities);

            fetchData();
        }
    }, [show, selectedItems, warehouse]);
    
    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!selectedWarehouse || !selectedLocation) {
            setError('Please select both warehouse and location');
            return;
        }
        
        // Create items to move with quantities from local state
        const itemsToMove = activeItems.map(item => ({
            id: item.id,
            quantity: parseFloat(itemQuantities[item.id]) || 0
        }));
        
        // Check if quantities are valid
        const hasInvalidQuantity = itemsToMove.some(item => 
            !item.quantity || 
            isNaN(item.quantity) || 
            item.quantity <= 0
        );
        
        if (hasInvalidQuantity) {
            setError('Quantity must be a positive number for all items');
            return;
        }
        
        try {
            setIsSubmitting(true);
            await inventoryItemAPI.moveBulk(itemsToMove, selectedLocation.value, comments);
            setIsSubmitting(false);
            onSuccess(); // Now only called after successful move
            onHide();
        } catch (error) {
            console.error('Error moving items:', error);
            setError(formatErrorResponse(error));
            setIsSubmitting(false);
        }
    };
    
    const handleQuantityChange = (id, value) => {
        // Update quantities locally without calling parent's onSuccess
        setItemQuantities(prev => ({
            ...prev,
            [id]: value
        }));
    };

    const handleRemoveItem = (itemId) => {
        // Don't allow removing the last item
        if (activeItems.length <= 1) {
            return;
        }
        
        // Remove item from activeItems
        setActiveItems(prev => prev.filter(item => item.id !== itemId));
        
        // Remove quantity entry for this item
        setItemQuantities(prev => {
            const newQuantities = { ...prev };
            delete newQuantities[itemId];
            return newQuantities;
        });
    };
    
    // Find item details safely
    const findItemDetail = (itemId) => {
        if (!Array.isArray(selectedItemDetails)) {
            return null;
        }
        
        // First try to find in selectedItemDetails
        const itemFromDetails = selectedItemDetails.find(detail => detail._id === itemId);
        if (itemFromDetails) {
            return itemFromDetails;
        }
        
        // If not found in selectedItemDetails, try to find in the current inventory items
        const itemFromInventory = inventoryItems?.find(item => item._id === itemId);
        if (itemFromInventory) {
            return itemFromInventory;
        }
        
        return null;
    };
    
    // Format options for react-select with proper ID handling
    const warehouseOptions = warehouses
        .filter(w => !systemSettings.enable_warehouse_transit || w._id === (warehouse?.id || warehouse?._id))
        .map(w => ({
            value: w._id,
            label: w.name
        }));

    // Filter out locations that are already occupied by selected items
    const currentLocationIds = new Set();
    selectedItemDetails.forEach(item => {
        if (item.location?._id) {
            currentLocationIds.add(item.location._id);
        }
    });
    
    const locationOptions = locations
        .filter(location => !currentLocationIds.has(location._id))
        .map(location => ({
            value: location._id,
            label: location.name
        }));
    
    // Check permission
    if (show && !hasPermission('inventory.move')) {
        return (
            <Modal show={show} onHide={onHide}>
                <Modal.Header closeButton>
                    <Modal.Title>Permission Denied</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Alert variant="danger">
                        You don&apos;t have permission to move inventory items.
                    </Alert>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={onHide}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        );
    }

    // Early return if no warehouse information
    if (show && !warehouse && systemSettings.enable_warehouse_transit) {
        return (
            <Modal show={show} onHide={onHide}>
                <Modal.Header closeButton>
                    <Modal.Title>Error</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Alert variant="danger">
                        No warehouse information available. Please select items from the same warehouse.
                    </Alert>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={onHide}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        );
    }
    
    return (
        <Modal 
            show={show} 
            onHide={() => {
                setItemQuantities({});
                setSelectedItemDetails([]);
                setError('');
                onHide();
            }} 
            size="xl"
        >
            <Form onSubmit={handleSubmit}>
                <Modal.Header closeButton className="bulk-modal-header">
                    <Modal.Title>
                        <i className="bi bi-arrows-move bulk-modal-icon"></i>
                        Move Items
                        <span className="bulk-modal-badge bulk-modal-badge-primary">
                            {activeItems.length}
                        </span>
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="mb-4">
                        {isLoading ? (
                            <div className="text-center py-4">
                                <Spinner animation="border" role="status">
                                    <span className="visually-hidden">Loading item details...</span>
                                </Spinner>
                            </div>
                        ) : (
                            <div className="bulk-table-wrapper">
                                <table className="table table-sm align-middle bulk-table bulk-table-primary">
                                    <thead>
                                        <tr>
                                            <th>GTIN</th>
                                            <th>Description</th>
                                            <th>Current Location</th>
                                            <th>Available</th>
                                            <th style={{minWidth: '280px'}}>Quantity to Move</th>
                                            <th style={{width: '50px'}}></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {activeItems.map(item => {
                                            const itemDetail = findItemDetail(item.id);
                                            return (
                                                <tr key={item.id}>
                                                    <td>{itemDetail?.stock_item?.gtin ?
                                                        <code>{itemDetail.stock_item.gtin}</code> :
                                                        <span className="text-warning">Item not in current view</span>}</td>
                                                    <td>{itemDetail?.stock_item?.description || 
                                                        <span className="text-warning">Item not in current view</span>}</td>
                                                    <td>{itemDetail?.location_name || 
                                                        <span className="text-warning">Item not in current view</span>}</td>
                                                    <td>{itemDetail?.quantity || 
                                                        <span className="text-warning">Item not in current view</span>}</td>
                                                    <td>
                                                        <QuantityPicker
                                                            casePack={itemDetail?.stock_item?.case_pack || 1}
                                                            uom={itemDetail?.stock_item?.uom || ''}
                                                            value={itemQuantities[item.id] || ''}
                                                            onChange={(qty) => handleQuantityChange(item.id, qty)}
                                                            required
                                                            max={itemDetail?.quantity}
                                                        />
                                                    </td>
                                                    <td>
                                                        <button
                                                            type="button"
                                                            className="bulk-remove-btn"
                                                            onClick={() => handleRemoveItem(item.id)}
                                                            disabled={activeItems.length <= 1}
                                                            title={activeItems.length <= 1 ? "Cannot remove the last item" : "Remove item"}
                                                        >
                                                            <i className="bi bi-trash3"></i>
                                                        </button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                    
                    <div className="bulk-section-sep bulk-section-sep-primary">
                        <span className="bulk-section-sep-label">
                            <i className="bi bi-geo-alt me-1"></i> Destination
                        </span>
                    </div>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Warehouse</Form.Label>
                            {systemSettings.enable_warehouse_transit ? (
                                <div className="form-control bg-light">
                                    {warehouse ? warehouse.name : 'No warehouse selected'}
                                </div>
                            ) : (
                                <Select
                                    value={warehouseOptions.find(option => option.value === selectedWarehouse?._id)}
                                    onChange={(option) => {
                                        const selected = warehouses.find(w => w._id === option?.value);
                                        setSelectedWarehouse(selected || null);
                                        setIsInitialLoad(false); // User has explicitly changed warehouse
                                    }}
                                    options={warehouseOptions}
                                    isSearchable
                                    placeholder="Select..."
                                    className="react-select-container"
                                    classNamePrefix="react-select"
                                    isDisabled={isSubmitting}
                                />
                            )}
                            <Form.Text className="text-muted">
                                {systemSettings.enable_warehouse_transit
                                    ? "Items can only be moved within the same warehouse."
                                    : "Select any warehouse as the destination."}
                            </Form.Text>
                        </Form.Group>
                    </Col>

                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Location</Form.Label>
                            <Select
                                value={selectedLocation}
                                onChange={setSelectedLocation}
                                options={locationOptions}
                                isSearchable
                                placeholder={locationOptions.length === 0 ? "No available locations (items already at all locations)" : "Select location"}
                                className="react-select-container"
                                classNamePrefix="react-select"
                                isDisabled={!selectedWarehouse || locations.length === 0 || locationOptions.length === 0}
                            />
                            {locationOptions.length === 0 && locations.length > 0 && (
                                <Form.Text className="text-warning">
                                    All selected items are already at all available locations in this warehouse.
                                </Form.Text>
                            )}
                        </Form.Group>
                    </Col>
                    
                    <Form.Group className="mb-3">
                        <Form.Label>Comments</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={3}
                            value={comments}
                            onChange={(e) => setComments(e.target.value)}
                            placeholder="Add any comments about this move..."
                            autoComplete="off"
                            spellCheck="true"
                        />
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    {error && <Alert variant="danger" className="mb-3 w-100">{error}</Alert>}
                    <Button variant="secondary" onClick={onHide} disabled={isSubmitting}>
                        Cancel
                    </Button>
                    <Button type="submit" variant="primary" disabled={isSubmitting || !selectedLocation}>
                        {isSubmitting ? (
                            <>
                                <Spinner
                                    as="span"
                                    animation="border"
                                    size="sm"
                                    role="status"
                                    aria-hidden="true"
                                    className="me-2"
                                />
                                Moving...
                            </>
                        ) : (
                            'Move Items'
                        )}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
}

export default BulkMoveModal; 